"use client";

import { useId, useState } from "react";
import Image from "next/image";
import { ArrowLeft, Smartphone } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ease } from "@/components/motion";
import { cn } from "@/lib/utils";
import type { Grade } from "@/lib/trust";

type Lift = { part: string; from: string[]; to: string[] };

// Tone steps from the brand teal (best) to plain surface (worst).
function tone(code: string) {
  if (code.startsWith("A")) return "bg-primary text-primary-foreground";
  if (code.startsWith("B")) return "bg-primary-container text-primary-on-container";
  if (code.startsWith("C")) return "bg-surface-strong text-foreground";
  return "bg-surface text-muted-foreground";
}

const or = (codes: string[]) => codes.join(" or ");

const wipe = {
  hidden: { clipPath: "inset(0 0 0 100% round 9999px)" },
  show: { clipPath: "inset(0 0 0 0% round 9999px)", transition: { duration: 1.2, ease, delay: 0.15 } },
};

/**
 * The grade scale, best to worst. Clicking a grade shows what it means and, for
 * grades we restore from, how far each part can go. Below, a bar per part wipes
 * in from the damaged grade toward the top as it scrolls into view.
 */
export function GradeScale({
  grades,
  lifts,
  initial = "C3",
  definitions = false,
}: {
  grades: Grade[];
  lifts: Lift[];
  /** Grade selected on load, so the panel shows an example straight away. */
  initial?: string;
  /** Also list every definition underneath (for the full standard page). */
  definitions?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const panelId = useId();
  const [selected, setSelected] = useState(initial);
  const codes = grades.map((g) => g.code);
  const cols = { gridTemplateColumns: `repeat(${codes.length}, minmax(0, 1fr))` };
  const grade = grades.find((g) => g.code === selected);
  const liftsFrom = lifts.filter((l) => l.from.includes(selected));

  return (
    <div>
      <p className="mb-3 text-sm text-muted-foreground">Select a grade to see what it means.</p>
      <ul aria-label="Grades, best to worst" className="grid gap-1.5 sm:gap-2" style={cols}>
        {codes.map((code) => (
          <li key={code}>
            <button
              type="button"
              aria-pressed={code === selected}
              aria-controls={panelId}
              onClick={() => setSelected(code)}
              className={cn(
                "flex h-12 w-full items-center justify-center rounded-lg text-sm font-medium tabular-nums transition-[box-shadow,transform] hover:-translate-y-0.5 motion-reduce:transform-none sm:h-14 sm:text-lg",
                tone(code),
                code === selected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
              )}
            >
              {code}
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex justify-between text-xs text-muted-foreground sm:text-sm">
        <span>Like new</span>
        <span>Barely functional</span>
      </div>

      {/* Details for the selected grade. Fixed min-height so switching doesn't jump the page. */}
      <div
        id={panelId}
        aria-live="polite"
        className="mt-6 min-h-[22rem] rounded-panel bg-surface p-4 sm:min-h-[16.5rem] sm:p-6 md:min-h-[14rem] md:p-8"
      >
        <AnimatePresence mode="wait" initial={false}>
          {grade && (
            <motion.div
              key={grade.code}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease }}
              className="grid grid-cols-[5.5rem_1fr] gap-x-4 gap-y-5 sm:grid-cols-[9rem_1fr] sm:gap-x-8 md:grid-cols-[10rem_1fr_1fr] md:gap-x-10"
            >
              <GradePhoto code={grade.code} src={grade.image} />
              <div>
                <p className="text-3xl tracking-tight tabular-nums">{grade.code}</p>
                <p className="mt-1 text-lg leading-relaxed text-muted-foreground">
                  {grade.summary ?? "Description coming soon."}
                </p>
              </div>
              {liftsFrom.length > 0 && (
                <div className="col-span-2 sm:col-span-1 sm:col-start-2 md:col-start-auto">
                  <p className="font-medium">What we can do</p>
                  <ul className="mt-2 space-y-1 text-lg text-muted-foreground">
                    {liftsFrom.map((l) => (
                      <li key={l.part}>
                        {l.part}: restored up to <span className="text-foreground">{or(l.to)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ul className="mt-10 space-y-8">
        {lifts.map((lift) => {
          const start = codes.indexOf(lift.to[0]);
          const end = Math.max(...lift.from.map((c) => codes.indexOf(c)));
          return (
            <li key={lift.part}>
              <p className="text-lg">
                <span className="font-medium">{lift.part}</span>
                <span className="text-muted-foreground">
                  : from {or(lift.from)}, restored up to {or(lift.to)}
                </span>
              </p>
              {/* The row (never clipped) triggers on scroll; the bar wipes in from the
                  damaged grade toward the result. clip-path keeps the labels unsquashed. */}
              <motion.div
                aria-hidden
                className="mt-3 grid gap-1.5 sm:gap-2"
                style={cols}
                initial={reduceMotion ? false : "hidden"}
                whileInView="show"
                viewport={{ once: true, amount: 0.8 }}
              >
                <motion.div
                  className="flex h-9 items-center justify-between rounded-full bg-primary px-3 text-sm font-medium text-primary-foreground sm:h-10 sm:px-4"
                  style={{ gridColumn: `${start + 1} / ${end + 2}` }}
                  variants={wipe}
                >
                  <span className="flex items-center gap-1.5">
                    <ArrowLeft className="size-4" />
                    {lift.to[0]}
                  </span>
                  <span className="text-primary-foreground/70">{lift.from.at(-1)}</span>
                </motion.div>
              </motion.div>
            </li>
          );
        })}
      </ul>

      {definitions && (
        <dl className="mt-14 grid gap-x-8 gap-y-6 border-t border-border pt-8 sm:grid-cols-2 lg:grid-cols-4">
          {grades
            .filter((g) => g.summary)
            .map((g) => (
              <div key={g.code}>
                <dt className="text-2xl tracking-tight tabular-nums">{g.code}</dt>
                <dd className="mt-1 leading-relaxed text-muted-foreground">{g.summary}</dd>
              </div>
            ))}
        </dl>
      )}
    </div>
  );
}

/** A phone at this grade, or a placeholder until the real photo is added in lib/trust.ts. */
function GradePhoto({ code, src }: { code: string; src?: string }) {
  return (
    <div className="relative aspect-[4/5] w-full self-start overflow-hidden rounded-2xl bg-surface-strong sm:row-span-2 md:row-span-1">
      {src ? (
        <Image src={src} alt={`A phone in grade ${code} condition`} fill sizes="10rem" className="object-cover" />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-2 border-2 border-dashed border-input text-muted-foreground [border-radius:inherit]">
          <Smartphone className="size-8" aria-hidden />
          <span className="px-2 text-center text-xs">Photo coming soon</span>
        </div>
      )}
    </div>
  );
}
